import React, { useState, useEffect, useMemo } from 'react';
import apiService from '../services/apiService';

const PublishHistory = () => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [expandedBatches, setExpandedBatches] = useState({});

    useEffect(() => {
        loadHistory();
    }, []);

    const loadHistory = async () => {
        try {
            setLoading(true);
            setError('');
            const res = await apiService.getPublishHistory(100);
            if (res.success) setHistory(res.data || []);
            else setError(res.error || 'Failed to load');
        } catch (e) {
            setError(e.response?.data?.error || e.message || 'Failed to load publish history');
        } finally {
            setLoading(false);
        }
    };

    const groups = useMemo(() => {
        const byBatch = new Map();
        for (const row of history) {
            const bid = row.ota_batch_id;
            if (bid) {
                if (!byBatch.has(bid)) byBatch.set(bid, []);
                byBatch.get(bid).push(row);
            }
        }
        const result = [];
        const seen = new Set();
        for (const row of history) {
            const bid = row.ota_batch_id;
            if (bid) {
                if (seen.has(bid)) continue;
                seen.add(bid);
                result.push({ type: 'batch', ota_batch_id: bid, rows: byBatch.get(bid) || [row] });
            } else {
                result.push({ type: 'single', row });
            }
        }
        return result;
    }, [history]);

    const toggleBatch = (id) => {
        setExpandedBatches((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="spinner mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading publish history...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-gray-900">Publish History</h1>
                <button
                    onClick={loadHistory}
                    className="text-sm text-primary-600 hover:text-primary-800"
                >
                    Refresh
                </button>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <p className="text-red-800">{error}</p>
                </div>
            )}

            <div className="bg-white shadow rounded-lg overflow-hidden">
                {history.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                        No publish events yet. Publish a firmware from the Firmware list to see history here.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Published At</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mode</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Batch / Target</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-36 max-w-[9rem]">Station</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Version</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">By</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Devices</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">OK</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {groups.map((g) => {
                                    if (g.type === 'single') {
                                        const row = g.row;
                                        return (
                                            <tr key={row.id} className="hover:bg-gray-50">
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                                    {apiService.formatDate(row.published_at)}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-xs">
                                                    <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                                                        {row.publish_mode || 'quick'}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                                    {row.target}
                                                    {row.ota_batch_id && (
                                                        <div className="text-xs font-mono text-gray-400 truncate max-w-[180px]" title={row.ota_batch_id}>
                                                            {row.ota_batch_id}
                                                        </div>
                                                    )}
                                                </td>
                                                <td
                                                    className="px-4 py-3 text-sm text-gray-600 font-mono max-w-[9rem] truncate"
                                                    title={row.station_id || undefined}
                                                >
                                                    {row.station_id || '—'}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{row.version}</td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{row.published_by}</td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                                    {row.devices_count != null ? row.devices_count : '—'}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                                    {row.success_count != null ? row.success_count : '—'}
                                                </td>
                                            </tr>
                                        );
                                    }

                                    const rows = g.rows;
                                    const first = rows[0];
                                    const totalDevices = rows.reduce((s, r) => s + (r.devices_count || 0), 0);
                                    const totalOk = rows.reduce((s, r) => s + (r.success_count || 0), 0);
                                    const expanded = !!expandedBatches[g.ota_batch_id];

                                    return (
                                        <React.Fragment key={g.ota_batch_id}>
                                            <tr
                                                className="hover:bg-primary-50 cursor-pointer bg-primary-50/40"
                                                onClick={() => toggleBatch(g.ota_batch_id)}
                                            >
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                                    {apiService.formatDate(first.published_at)}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-xs">
                                                    <span className="px-2 py-0.5 rounded-full bg-primary-100 text-primary-800">
                                                        {first.publish_mode || 'batch'} · {rows.length} station{rows.length !== 1 ? 's' : ''}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                                    <span className="mr-1">{expanded ? '▾' : '▸'}</span>
                                                    {first.target}
                                                    <div className="text-xs font-mono text-primary-600 truncate max-w-[220px]" title={g.ota_batch_id}>
                                                        {g.ota_batch_id}
                                                    </div>
                                                </td>
                                                <td
                                                    className="px-4 py-3 text-sm text-gray-600 font-mono max-w-[9rem] truncate"
                                                    title={
                                                        rows.map((r) => r.station_id).filter(Boolean).join(', ') ||
                                                        undefined
                                                    }
                                                >
                                                    {rows.map((r) => r.station_id).filter(Boolean).join(', ') || '—'}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{first.version}</td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{first.published_by}</td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{totalDevices}</td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{totalOk}</td>
                                            </tr>
                                            {expanded &&
                                                rows.map((row) => (
                                                    <tr key={row.id} className="bg-gray-50">
                                                        <td className="px-4 py-2 pl-8 text-xs text-gray-500" colSpan={2}>
                                                            {row.mqtt_topic || '—'}
                                                        </td>
                                                        <td className="px-4 py-2 text-xs text-gray-600">{row.target}</td>
                                                        <td
                                                            className="px-4 py-2 text-xs font-mono text-gray-700 max-w-[9rem] truncate"
                                                            title={row.station_id || undefined}
                                                        >
                                                            {row.station_id || '—'}
                                                        </td>
                                                        <td className="px-4 py-2 text-xs text-gray-500" colSpan={2}>
                                                            {row.topics_json
                                                                ? (() => {
                                                                      try {
                                                                          const t = JSON.parse(row.topics_json);
                                                                          return Array.isArray(t) ? `${t.length} topic(s)` : '—';
                                                                      } catch {
                                                                          return '—';
                                                                      }
                                                                  })()
                                                                : '—'}
                                                        </td>
                                                        <td className="px-4 py-2 text-xs text-gray-600">{row.devices_count ?? '—'}</td>
                                                        <td className="px-4 py-2 text-xs text-gray-600">{row.success_count ?? '—'}</td>
                                                    </tr>
                                                ))}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PublishHistory;
